import React from 'react';
import { Volume2, ChevronRight, Home, Mail, Users, Landmark, FileText, CheckCircle2 } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface RobertsPart5SectionProps {
  onSelectPage?: (pageId: string) => void;
}

export const RobertsPart5Section: React.FC<RobertsPart5SectionProps> = ({ onSelectPage }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 text-[#2c221e] font-sans">
      {/* Top Breadcrumb / Page Header */}
      <header className="bg-[#f3ede0] p-6 sm:p-10 md:p-12 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          <div className="flex items-center gap-1.5 font-bold">
            {onSelectPage && (
              <button
                onClick={() => onSelectPage('hub')}
                className="hover:underline flex items-center gap-1 text-[#7a5b3a] cursor-pointer"
              >
                <Home className="w-3.5 h-3.5" />
                <span>The Roberts Family</span>
              </button>
            )}
            {onSelectPage && <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />}
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full uppercase tracking-wider text-[11px] border border-[#d8ceb9]">
              Part 5 of 5
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={speakCurrentPage}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
              title="Listen to Part 5: Robert Morris Roberts and Troqueer"
            >
              <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
              <span>{isPlaying ? 'Listening to Part 5...' : 'Listen to Part 5'}</span>
            </button>
          </div>
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight">
          ROBERT MORRIS ROBERTS - his family has been farming on Troqueer for more than a century
        </h1>
        <p className="font-serif text-base sm:text-lg text-[#7a5b3a] italic leading-relaxed">
          From Motunau childhood and Happy Valley shearing to Starborough, Blind River, Troqueer Farm, and authentic archival correspondence
        </p>
      </header>

      {/* Main Archival Article */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base leading-relaxed text-[#2c221e]">
        
        {/* Intro Section */}
        <section className="space-y-6">
          <p>
            Robert Morris Roberts (1874-1960) was the fourth child of Robert Morris and Sarah Elizabeth Roberts. His first four years were at the Taramakau Accommodation House, then the family moved to Cabbage Tree Flat in the Hurunui area where those early years were so wonderfully portrayed by his older brother T.E.L<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p5-1">1</a></sup>. and younger brother Edward.<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p5-2">2</a></sup>
          </p>

          <p>
            Their childhood on the hills and gullies of Motunau were busy and there were many activities young boys in particular could enjoy - eeling in the creeks, riding pigs, raiding bird nests, running errands on horseback or foot across the valley, doing chores for some money, helping with real jobs on the land and all the time learning rural skills. They seemed to get along so well together, with real affection.
          </p>

          <p>
            In 1886 Morris was living with Fanny and Jim Pain<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p5-3">3</a></sup> at Sefton in order to attend school, in Standard One (Year 3).
          </p>

          <p>
            When Morris was 16 he bought a violin during the shearing season, where he earned money fleece picking at Cheviot Hills. "When Morris came home with the fiddle Father said that is the most useless thing that has come into this house. None of the family will ever get a tune out of it. But Tom became quite an efficient musician and gave pleasure to Father as well as all the other members of the family.'
          </p>

          <p>
            Young Morris worked clearing gorse and broom at Happy Valley, painted fence posts with tar and fenced paddocks, grubbed a lot of gorse plants, learned to shear and was employed by many farmers.
          </p>

          <p>
            His 21st birthday party was celebrated at Happy Valley on 5 October 1895. He and his brother Baxter were working on Stoneyhurst at that time. Morris rode to the event on Queeny, George Gibb's hack lent for the occasion. He was clearly a competent shearer as many local farmers sought him for their flocks. Shearing usually started at 5.30 a.m. with breaks for a cooked breakfast at 8 a.m. then a morning tea break, lunch, an afternoon break and finish for the day at 5.30 pm.
          </p>

          <p>
            "My brother Morris could count on his fingers the number of alcoholic drinks he had during his life but he was often humorous," Ted wrote.
          </p>

          <p>
            "The day Sir Joseph Ward opened the railway at Seddon in October 1902 Morris happened to attend and being a little tired after some days in the shearing shed stretched out on a patch of grass during a lull in the celebrations and was making some jokes to his friends. Mrs Bill Sutherland, wife of a Blind River farmer, happened to see him on the ground and next day said to young Jenny Trueman, wasn't Morris Roberts drunk yesterday at the opening of the railway? "Was he" enquired Jenny. "Yes", said Mrs Sutherland, "he was so drunk he could not stand up but was lying on the ground laughing and going on in grand style".
          </p>

          <p>
            After a shearing season in the Awatere Morris and Tom worked stacking oats and wheat. Morris did the stacking and Ted worked on the dray, from 7 am to 6 pm each day, for 7 shillings a day plus board. Then they went shearing lambs.
          </p>

          <p>
            Stewart Gibb had given Morris a young bitch and one of her pups. She was named Fly, from a Scottish Collie that a local man, Bob Scott, had brought back from Scotland in about 1897 after being in charge of a live shipment of fat lambs to England. They were eye dogs and Morris was warned to always be on the lookout in case they took to worrying sheep. However, Fly's pup Whiskers was liable to grab a lamb and kill it. His destiny was inevitable, at the cost of a bullet, and the wrath of his young wife.
          </p>

          <p>
            In the new century, Edward wrote of the exciting development in Morris' life. "Early in 1901 I had a bad attack of flue. My brother Morris was down on holiday from Starborough and when he called at The Flat showed me a photo of Phoebe Armstrong<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p5-4">4</a></sup>. She and her sister Kate had been up at Starborough for a time keeping house for their brother George who had the Star Hill section. Morris had become engaged to her before she returned to Loburn."
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              Tom wrote about the sea trip he and Morris took from Lyttelton to Wellington and experienced sea sickness. They booked into the City Hotel Wellington, Room 38. "Neither Morris or I knew much about hotel life and I at any rate knew nothing about inside toilets. We went to bed early and made good use of the kitchen utensils. We put our boots in the passage before retiring about 8 pm. We heard no complaints until we were ascending the stairs next morning about 11.30, after having a walk round the streets, where Morris had stepped into some slush swept up by the street cleaners. A rather unfriendly woman asked if we had slept in Room 38 and as Morris was the elder I let him answer the question, and the old girl opened up on us with I rather think you did and the idea of going to bed at 8pm and putting your boots out to be cleaned at that time of night and now look at them. I rather think that riled her was having to empty the bedroom utensils we had used instead of going to the toilet."
            </p>

            <p>
              As the two country lads strolled along Lambton Quay they saw a sweet shop. Morris remembered that Phoebe's mother had a friend who had a lolly shop in Wellington, so they went in, and it was that same friend -she gave them a bag of sweets for Mrs Armstrong.
            </p>

            <p>
              "During the afternoon we sailed on the steamer Wainui for Picton, Morris travelling salon and I steerage. We had a meal before reaching Picton. Morris in the saloon had the best of food with oranges on the table for dessert while in second class I had stew and potatoes cooked in their jackets."
            </p>

            <p>
              They went to Blenheim and stayed overnight in a boarding house, leaving after breakfast to walk to Seddon, along the railway line. They called in on Jim and Lottie (nee Aroa) who gave them a meal and then put a horse in the buggy to drive them to Star Hill beyond Seddon. Just as they arrived George Armstrong was about to leave so Tom got a ride with him. They stayed the night with the Armstrongs and then walked over to Brooklyn, Tom's 360 ha farm at Tetley Brook.
            </p>

            <p>
              "It was near noon when we arrived and when we got inside the house T.E.L. got a bit excited about going to the North Island and began putting things together to take with him and Morris."
            </p>
          </blockquote>

          <p>
            Morris and Tom were considering buying adjoining larger properties in the Pongakawa district, near Te Puke and they were heading north to investigate. It was dense fern land that needed clearing and they abandoned that idea after about a month. They had made the trip north from Wellington on a ship that was sailing for Onehunga, calling in at Nelson and New Plymouth on the way and then from Auckland they sailed on a coastal vessel to the Bay of Plenty. Tom said he would pay the expenses for the venture.
          </p>

          <p>
            Morris was looking for work so he did some road fencing with rabbit netting for 10 shillings a day, and then he wanted to get back closer to his fiancée.
          </p>

          <p>
            In 1901 Morris bought his first farm adjoining Blind River, 280 ha just north of Lake Grassmere. It was an area of about 280 ha, with a lease for 999 years. The southern boundary was Seddon - Blind River Road. There was a netting fence on the eastern boundary. Morris agreed to pay 450 Pounds for the goodwill. There was a house with two rooms, unlined, made of corrugated iron and with no chimney Morris had two rooms added to the front, connected by a passage. A double chimney served for the range in the old part and a grate in the extension heated the sitting room. The other new room was a bedroom. Those additions cost 140 Pounds.
          </p>

          <p>
            As well as managing his own stock Morris worked off farm, fencing and shearing.
          </p>

          <p>
            A year later, aged 29, he married Phoebe Armstrong (26), at Loburn. He asked his sister, Lizzie<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p5-5">5</a></sup> if her two eldest daughters, Helen and Annie, would be flower girls - an honour they cherished and which Annie remembered all her life.
          </p>

          {/* Image 222 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/222 Helen and Annie Gibb 1903.jpg"
              alt="Helen and Annie Gibb 1903"
              caption="Helen and Annie Gibb 1903"
              filenameHint="222 Helen and Annie Gibb 1903.jpg"
            />
          </figure>

          <p>
            Phoebe's first child, a son, died a few days after birth in the Blenheim nursing home. For her second confinement she stayed at home under the care of Dr Bernard Volkman. The baby girl developed jaundice after birth and Phoebe was distressed, "Dr Volkman told her he knew how to treat the child but did not know how to deal with her. However the baby and mother were soon both fit and well, the baby from the jaundice and the mother from her anxiety. I was asked to choose a name for the baby and chose Ethel after Ethel Hoan of Morton Mains<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p5-6">6</a></sup> and Morris and Phoebe added Margaret after my sister Margaret."
          </p>

          <p>
            Morris sold his farm in 1910 as a going concern to J. Costello of Seaview for 4500 Pounds and bought Run 176 from J. Muir containing 1370 acres, for 3750 Pounds, with the sheep included. Mr Muir had named it Troqueer after a town in south west Scotland. It is inland from Ward on Needles Road, off SH1.<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p5-7">7</a></sup>
          </p>

          <p>
            At that time the young couple had four children, and the two girls were sent to live with Phoebe's sister at Saltwater Creek for two years while they got established. Phoebe looked after a young baby boy and his brother in this very basic dwelling, but it was not for too long.
          </p>

          <p className="font-bold font-serif text-lg text-[#2c221e]">
            The first dwelling on Troqueer
          </p>

          {/* Images 223 and 224 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/223 First dwelling on Troqueer 1910.jpg"
              alt="First dwelling on Troqueer 1910"
              caption="First dwelling on Troqueer 1910"
              filenameHint="223 First dwelling on Troqueer 1910.jpg"
            />
            <ArchivalImageCard
              src="public/images/224 Second dwelling on Troqueer 1911.jpg"
              alt="Second dwelling on Troqueer 1911"
              caption="Second dwelling on Troqueer 1911"
              filenameHint="224 Second dwelling on Troqueer 1911.jpg"
            />
          </div>

          <p>
            Ted and George Roberts put in a boundary fence for their brother, which did not need attention for 40 years, and did fencing and shearing for other local farmers. For the harvest season they worked on the oats. Algerian oats were heavy in straw and grain and there was a good demand for oats and chaff until motor cars replaced horse transport.
          </p>

          <p>
            In 1911 a battle cruiser "New Zealand", a gift from NZ to the British Navy, toured NZ. Morris and Ted went by a special excursion train from Ward to Picton, had lunch in a hotel then went by smaller boat out to tour the battleship which was anchored off Mabel Island.
          </p>

          <p>
            Morris had one of his first rides in a motor car in 1914 when Eddie Trueman drove him down in his new Model T Ford for George Robert's funeral at Waikari. Ted drove Morris back to Plas Isa after the service in his horse and gig and it seemed very slow to Morris!
          </p>

          <p>
            Family members had holidays with Morris and Phoebe at Troqueer for many years. Burnie Gibb went there on his motorcycle and stayed a short time, going rabbiting up the Needles Creek. Morris and George were both crack shot at rabbits.
          </p>

          {/* Image 225 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/225 Phoebe and Robert Morris Roberts with Greta, Constance, Frank, Marhjorie and Gordon.jpg"
              alt="Phoebe and Robert Morris Roberts with family"
              caption="Ethel, Greta Constance, Robert Morris & Phoebe, Frank, baby Marjorie and Gordon, about 1912"
              filenameHint="225 Phoebe and Robert Morris Roberts with Greta, Constance, Frank, Marhjorie and Gordon.jpg"
            />
          </figure>

          <p>
            Robert and Phoebe gave their individual sheep returns in 1928, and they were published in the Journal of the House of Representatives. Phoebe had 1376 sheep and Robert had 1830.
          </p>

          <p>
            In 1970 the Troqueer Farm Ltd was formed. Robert Morris Roberts (son of RM and Phoebe Roberts, grandson of Robert Morris Roberts of Plas Isa) and Maureen Murial Roberts retired from the farm in 2008 to Blenheim and enjoy their Port Underwood retreat. His son Duncan Morris Roberts and daughter in law Trudi bought into the company in 2005 and are now the shareholders.
          </p>

          <div className="space-y-2 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
            <h3 className="font-serif font-bold text-lg text-[#2c221e]">
              Phoebe and Robert's children:
            </h3>
            <ul className="space-y-1 text-sm sm:text-base text-[#2c221e] pl-4 list-disc">
              <li>Ethel Margaret</li>
              <li>Gordon (1907-1991)</li>
              <li>Greta Constance Kennington (1908-1992)</li>
              <li>Frank Morris (1910-1983) took over the farm in the 1930's and lived on the farm until his death.</li>
              <li>Marjory Edna McNabb</li>
            </ul>
          </div>

          {/* Images 226 and 227 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/226 Gordon, Marjorie, Ethel and Greta with Phoebe and Robert Morris Roberts of Troqueer.jpg"
              alt="Gordon, Marjorie, Ethel and Greta with Phoebe and Robert Morris Roberts of Troqueer"
              caption="Gordon, Marjorie, Ethel and Greta with Phoebe and Robert Morris Roberts of Troqueer"
              filenameHint="226 Gordon, Marjorie, Ethel and Greta with Phoebe and Robert Morris Roberts of Troqueer.jpg"
            />
            <ArchivalImageCard
              src="public/images/227 Frank, Marjorie, Robert Morris Roiberts, Greta, Gordon and Ethel.jpg"
              alt="Frank, Marjorie, Robert Morris Roberts, Greta, Gordon and Ethel"
              caption="Frank, Marjorie, Robert Morris Roberts, Greta, Gordon and Ethel"
              filenameHint="227 Frank, Marjorie, Robert Morris Roiberts, Greta, Gordon and Ethel.jpg"
            />
          </div>
        </section>

        {/* Section: Extracts from a letter written by Morris and Phoebe Roberts (1906) */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <div className="flex items-center gap-2 text-[#7a5b3a]">
            <Mail className="w-5 h-5" />
            <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              Extracts from a letter written by Morris and Phoebe Roberts (1906)
            </h2>
          </div>

          <div className="space-y-4">
            <h3 className="font-serif font-bold text-lg text-[#7a5b3a]">
              Phoebe
            </h3>
            <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base leading-relaxed text-[#2c221e] space-y-3">
              <p>
                "We have two cows milking. The little foal takes all one gives. We feed him five times a day on new milk, he is doing well, keeping quite fat, and the calf and him are great mates. I have to watch Ethel that she doesn't get near to them, for they would knock her over in a minute.
              </p>
              <p>
                Morris will be going down to the Exhibition<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p5-8">8</a></sup> in the autumn. I won't be going down this year. Ted and Miss Truman intend going after harvest. I think this is all I have to write about.....
              </p>
            </blockquote>
          </div>

          <div className="space-y-4">
            <h3 className="font-serif font-bold text-lg text-[#7a5b3a]">
              Morris
            </h3>
            <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base leading-relaxed text-[#2c221e] space-y-3">
              <p>
                The rabbits are increasing very fast this year, so I want to stop the neighbours from coming in while I kill my own. I have killed over 200 since I came back from Canterbury. We are expecting a visit from George Gibb any time now. I hope he stays a few days when he comes. Phoebe and the baby are keeping in the very best of health. Baby can't talk yet, but she is pretty good at eating apple pie and sleeping at night. She wakes early in the morning and pulls my hair and whiskers.
              </p>
              <p>
                I was in Blenheim last week and bought a 100 lb of apples for 15/-. I got them off an old Irish lady and it was worth half the money to hear her talk. I sold my young mare (the unbroken one) to Frank Thorpe yesterday for £23. Ted is down seeing the Old Man tonight, he is nearly always down there now, he will be 25 years old tomorrow. It doesn't seem long since he was a little babe in the Old Sod house at the flat.
              </p>
              <p>
                Phoebe wants to lay the table for dinner, so goodbye with love to all. I hope this finds you all in the best of health as it leaves us. Goodbye with love to all.
              </p>
              <p className="font-bold">
                Phoebe and Morris
              </p>
              <p className="italic text-sm">
                P.S.<br />
                Will write better next time. Baby is in good form.
              </p>
            </blockquote>
          </div>
        </section>

        {/* Section: Letters from Morris and Phoebe written 3 August 1909 */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <div className="space-y-2">
            <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              Letters from Morris and Phoebe written 3 August 1909
            </h2>
            <p className="text-sm font-serif italic text-[#7a5b3a]">
              Morris started writing to his brother Edward, who was in his first year living on Riverside, Culverden, with his wife Jennie (Sarah Jane Trueman) on 3 August.
            </p>
          </div>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base leading-relaxed text-[#2c221e] space-y-4">
            <p className="font-bold text-lg">
              Dear Ted
            </p>
            <p>
              I've been talking of writing to you for some time now but somehow or other it always ended there. We had Brown's man here ploughing for 3 weeks so have been playing cards at night. He's gone now, ploughed about 45 acres. I put a gateway in the netting fence for him to come through. I think I will put mangolds in 15 acres and rape in the rest. I'm going to put lucerne in that flat up by Trigg's Hit. I'm fencing it off now - nothing like plenty of lucerne. The 8 acres I have is looking lovely, about 3 inches high now.
            </p>
            <p>
              Best winter I've seen. Grass never stopped growing. I've bought a double furrow plough off J. Waters for 8 Pounds. I'm going to break in the motherless foal, and plough with 3, the plough is a light one so I think they will pull it. Waters bought 5 ton of chaff off me @ 2 Pounds per ton, me paying the cutting, so I had to take something out. I had Cyril Waters working for me for 5 weeks but had to get rid of him. You know Ted, it wasn't safe to leave him by himself-he would nearly burst himself.
            </p>
          </blockquote>

          {/* Image 228 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/228 Extract from letter written by Phoebe Roberts.jpg"
              alt="Extract from letter written by Phoebe Roberts"
              caption="Extract from letter written by Phoebe Roberts"
              filenameHint="228 Extract from letter written by Phoebe Roberts.jpg"
            />
          </figure>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base leading-relaxed text-[#2c221e] space-y-4">
            <p>
              Pat Donaghue has had two big court cases with Bell the coach driver and lost both of them. All about chaff. Pat was to supply him with good prime chaff @ 4 Pounds 12 and 6 per ton. He cut him that stack, which he meant to take a stack with the top you cut, of course it was wet and Bell wouldn't take it. Pat swore in court that all his stacks were thatched. Pat reckoned Old Scott Smith has his knife into him, so appealed, but Judge Copper must have had had the knife in him too, for he decided in favour of Bell.
            </p>
            <p>
              Mr Blick cut his foot very bad with an axe 3 or 4 weeks ago, he is at Mrs Hall's nursing home. I suppose he will do a bit of courting while it is getting better.
            </p>
            <p>
              Mr Hall has left the Brook and young Richmond has taken over the management. The Awatere people gave Old Hall a smoke concert and a gold watch and chain, as a sort of memento for shearing wet sheep with scissors. It's great to be a hero.
            </p>
            <p>
              The line is nearly finished to Grassmere now and the bank is getting well over the Lake so it wont be long before they reach Flaxbourne.
            </p>
            <p>
              Well, I don't seem to be able to think of any more tonight and Phoebe has got to write to Jennie so I must leave her something to say so goodbye till I see you again, Morris.
            </p>
          </blockquote>

          {/* Image 229 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/229 Letter to Jennie Gibb.jpg"
              alt="Letter to Jennie Gibb"
              caption="Letter to Jennie Gibb"
              filenameHint="229 Letter to Jennie Gibb.jpg"
            />
          </figure>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base leading-relaxed text-[#2c221e] space-y-4">
            <p>
              We like Mr Stacy the best yet, he always seems the same. We had Mr &amp; Mrs Sharp and the Miss Fosters at the last service down here so Blind River is looking up. Nettie Waters has just got back from spending her winter holiday in Wellington, so she is a young lady of some importance now, that are all at home. Mrs Waters gets very little carting to do these days, Ella often comes up to see us, Morrie says she has less sense than ever. Mrs Waters has the best of health now. Mrs Rodgerson has been round nursing Mrs Webster again. Mrs Willie Fleming was still waiting in Blenheim the last we heard. You will know more about Mrs Hawker than I do. Dove went into town the last time we did. She always travels first class and gives herself first class airs, she would never think of stopping to speak to us.....
            </p>
          </blockquote>

          {/* Image 230 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/230 Letter extract about school scandal.jpg"
              alt="Letter extract about school scandal"
              caption="Letter extract about school scandal"
              filenameHint="230 Letter extract about school scandal.jpg"
            />
          </figure>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base leading-relaxed text-[#2c221e] space-y-4">
            <p>
              Mrs G Donoghue will soon have another arrival. I hardly ever see her, she doesn't mix with Blind River people. Mr Andrews is to be married on the 18<sup>th</sup> of this month, the place looks so nice now. Morrie said he had given Ted all the news, just left me the gossip, so I think I have done that now. Mrs Orchard has Mrs Stuart staying with her again, they were up last week. Mrs Orchard doesn't, she always tries to be so brave, even when the tears are in her eyes.
            </p>
            <p>
              Our children are all well, baby is a lazy wee girl not walking on her own yet, she seems quite content to crawl. I am going to make Gordon his first suit this week, so he will be a man after this. Ethel's growing such a big girl, I don't think she has quite forgotten her Auntie yet. We have had such a lovely winter, scarcely any frost, the spring flowers are all out now, and the crops are so green. Morrie has been busy fencing most of the winter, we are going to keep our own team after this. We have no lambs yet, but most people have some now.
            </p>
            <p>
              I simply can't think of a thing more to write about so Goodbye with fondest love<br />
              <span className="font-bold">Phoebe</span>
            </p>
          </blockquote>
        </section>

        {/* Series Completion Celebration Banner */}
        <section className="bg-emerald-900/10 border-2 border-emerald-800/30 p-6 sm:p-8 rounded-3xl space-y-3">
          <div className="flex items-center gap-2 text-emerald-800 font-serif font-bold text-lg">
            <CheckCircle2 className="w-5 h-5 text-emerald-700" />
            <span>The Roberts Family: 5-Part Historical Archive Complete</span>
          </div>
          <p className="text-sm text-[#4a3525] leading-relaxed">
            All 5 comprehensive parts of The Roberts Family series have now been published verbatim with complete archival imagery, authentic correspondence, transcribed memoirs, poems, and footnotes.
          </p>
        </section>

        {/* FOOTNOTES BLOCK */}
        <section className="pt-8 border-t-2 border-[#d8ceb9] space-y-3 text-xs text-[#5c4328]">
          <div className="flex items-center gap-1.5 font-serif font-bold text-sm text-[#7a5b3a] mb-2">
            <Landmark className="w-4 h-4" />
            <span>Footnotes &amp; Archival Citations (Part 5)</span>
          </div>

          <ol className="space-y-1.5 pl-5 list-decimal">
            <li id="fn-p5-1">
              Thomas Edward Lloyd Roberts, called Tom in his books, poems and writings
            </li>
            <li id="fn-p5-2">
              Edward Roberts: A Personal History
            </li>
            <li id="fn-p5-3">
              Fanny was the youngest half sister of his mother, Sarah
            </li>
            <li id="fn-p5-4">
              Phoebe Roberts, nee Armstong 1877-1933
            </li>
            <li id="fn-p5-5">
              Eliza Catherine Roberts married James Lindsay Gibb
            </li>
            <li id="fn-p5-6">
              Tom had spent some time in Southland and developed life long friendships from th epoeple he met there.
            </li>
            <li id="fn-p5-7">
              Troqueer remains in Roberts family ownership. Troqueer Farm Cottage is
            </li>
            <li id="fn-p5-8">
              Christchurch International Exhibition, 1906-7
            </li>
          </ol>
        </section>
      </article>

      {/* Footer Navigation */}
      <footer className="flex flex-wrap items-center justify-between gap-4 p-6 bg-[#f3ede0] rounded-2xl border border-[#d8ceb9]">
        <div className="flex items-center gap-2">
          {onSelectPage && (
            <button
              onClick={() => onSelectPage('part4')}
              className="flex items-center gap-2 px-4 py-2 bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] font-serif font-bold text-xs rounded-xl transition cursor-pointer"
            >
              <span>&larr; Previous: Part 4 (Starborough &amp; T.E.L.)</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {onSelectPage && (
            <button
              onClick={() => onSelectPage('all')}
              className="flex items-center gap-2 px-4 py-2 bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-xs rounded-xl transition cursor-pointer"
            >
              <span>View Entire 5-Part Roberts Family Archive &rarr;</span>
            </button>
          )}
        </div>
      </footer>
    </div>
  );
};
